/* hs-eslint ignored failing-rules */

/* eslint-disable react-hooks/exhaustive-deps */
'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import classNames from 'classnames';
import iconAliases from 'icons/iconAliases';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import devLogger from 'react-utils/devLogger';
import memoize from 'react-utils/memoize';
import styled from 'styled-components';
import memoWithDisplayName from '../utils/memoWithDisplayName';
import { getIconNamePropType } from '../utils/propTypes/iconName';
import { ICON_SIZES } from './IconConstants';

const sizeToRes = size => size <= ICON_SIZES.small ? 'low' : 'high';

const getIconClassName = (size, measuredRes) => {
  const res = size != null ? sizeToRes(ICON_SIZES[size] || size) : measuredRes;
  return `private-icon__${res}`;
};

const getComputedFontSize = size => {
  if (!size) return null;

  if (ICON_SIZES[size]) {
    return ICON_SIZES[size];
  }

  return size;
};

const IconContent = styled.span.withConfig({
  displayName: "UIIcon__IconContent",
  componentId: "sc-1bga3h0-0"
})(["font:inherit !important;&::before{content:'", "';}"], ({
  ligature
}) => ligature);
const getIconContent = memoize(ligature => /*#__PURE__*/_jsx(IconContent, {
  "aria-hidden": true,
  ligature: ligature
}));
let hasPingedNewRelic = false;
const UIIcon = memoWithDisplayName('UIIcon', props => {
  const {
    className,
    color,
    name,
    screenReaderText,
    style,
    size
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["className", "color", "name", "screenReaderText", "style", "size"]);

  const [measuredRes, setMeasuredRes] = useState('low');
  const measureRef = useRef(null);
  useEffect(() => {
    if (size) return; // Measure the `fontSize` in the DOM to determine whether this is high- or low-res.

    const styles = getComputedStyle(measureRef.current, null); // Patch for Firefox < 62: https://bugzilla.mozilla.org/show_bug.cgi?id=1467722

    if (!styles) return;
    const newMeasuredRes = sizeToRes(parseInt(styles.fontSize, 10));
    if (newMeasuredRes !== measuredRes) setMeasuredRes(newMeasuredRes);
  });

  if (iconAliases[name]) {
    if (window.newrelic && !hasPingedNewRelic) {
      hasPingedNewRelic = true;
      window.newrelic.addPageAction('icon-alias', {
        alias: name
      });
    }

    if (process.env.NODE_ENV !== 'production') {
      devLogger.warn({
        message: `UIIcon: "${name}" is a deprecated alias. Please use the canonical icon name.`,
        key: `UIIcon-${name}`,
        url: 'https://tools.hubteamqa.com/ui-library/styles/icons'
      });
    }
  }

  const computedName = iconAliases[name] || name;
  const computedClassName = classNames('private-icon', getIconClassName(size, measuredRes), className);
  return /*#__PURE__*/_jsxs("span", Object.assign({}, rest, {
    className: computedClassName,
    "data-icon-name": computedName,
    ref: measureRef,
    style: Object.assign({
      color
    }, style, {
      fontSize: getComputedFontSize(size)
    }),
    children: [getIconContent(computedName), screenReaderText &&
    /*#__PURE__*/
    // Spaces around screenReaderText prevents collisions withsiblingtext.
    _jsxs("span", {
      className: "sr-only",
      children: [" ", screenReaderText, " "]
    })]
  }));
});
UIIcon.propTypes = {
  color: PropTypes.string,
  name: getIconNamePropType().isRequired,
  screenReaderText: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.oneOf(Object.keys(ICON_SIZES)), PropTypes.number])
};
export default UIIcon;